import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { dropTask } from 'ember-concurrency';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import RadioGroup from 'tio-ui/components/radio-group';
import Text from 'tio-ui/components/text';
import { presentationForSlrLoanStanding } from 'tio-employee/utils/slr-loan-standing-verification';
import {
  PhCaretDown,
  PhCaretUp,
  PhCheckCircle,
  PhSealWarning,
  PhSparkle,
} from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { SlrLoanStandingAuditState } from 'tio-employee/types/slr-loan-standing-verification';

interface LoanStandingAttentionCardSignature {
  Args: {
    state: SlrLoanStandingAuditState;
    onOpenUpload: () => void;
    /**
     * Records the participant's answer from the inline Review Name panel
     * (fuzzy name match). Resolves once the settled state is applied.
     */
    onConfirmName?: (confirmed: boolean) => Promise<void> | void;
  };
  Element: HTMLDivElement;
}

// Reasons that switch the instruction panel to the NSLDS second-document
// variant: the first document was readable but reported a federal
// non-repayment status (e.g. deferment), so only an official NSLDS file can
// verify the account. Source: tio-api good_standing_reason +
// ObservableDocument pipeline codes (see utils/slr-loan-standing-verification).
const NSLDS_REASONS = new Set(['nslds_required_for_federal_non_repayment_status']);

/**
 * Parse a date string, treating date-only values (YYYY-MM-DD) as LOCAL dates —
 * `new Date('2026-12-01')` is UTC midnight, which renders the previous
 * day/month in western timezones. Returns null for unparseable input.
 */
function parseLocalish(value: string | null | undefined) {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * The SLR dashboard's "Needs Your Attention" card for loan-standing
 * verification, per the Figma spec (SLR // Loan Standing Verification):
 * headline + optional required-by chip, collapsible instruction panel
 * (initial document checklist, or the NSLDS second-document variant), the
 * ineligibility note, and the Required Documents row that opens the upload
 * drawer. Scoped upstream to the contribution-receiving account only — this
 * component never sees other accounts' standing.
 */
export default class LoanStandingAttentionCard extends Component<LoanStandingAttentionCardSignature> {
  @service declare intl: IntlService;

  @tracked isExpanded = true;

  // Inline Review Name panel (Figma v3 node 1333-3886): the confirm CTA opens
  // the panel in place of the document row; a radio answer enables Confirm.
  @tracked isReviewPanelOpen = false;
  @tracked reviewAnswer: 'yes' | 'no' | undefined;

  get reviewOptions() {
    return [
      { key: 'yes', label: this.intl.t('slr.loan_standing.drawer.confirm_name_yes') },
      { key: 'no', label: this.intl.t('slr.loan_standing.drawer.confirm_name_no') },
    ];
  }

  openReviewPanel = () => {
    this.reviewAnswer = undefined;
    this.isReviewPanelOpen = true;
  };

  closeReviewPanel = () => {
    this.isReviewPanelOpen = false;
    this.reviewAnswer = undefined;
  };

  setReviewAnswer = (value: 'yes' | 'no') => {
    this.reviewAnswer = value;
  };

  get isConfirmDisabled() {
    return !this.reviewAnswer || this.confirmSelection.isRunning;
  }

  confirmSelection = dropTask(async () => {
    if (!this.reviewAnswer) return;
    await this.args.onConfirmName?.(this.reviewAnswer === 'yes');
    this.closeReviewPanel();
  });

  /** The confirm variant opens the inline panel; every other ask opens the drawer. */
  get primaryCtaAction() {
    return this.isNameConfirmVariant ? this.openReviewPanel : this.args.onOpenUpload;
  }

  get isNsldsVariant() {
    return NSLDS_REASONS.has(this.args.state.primaryReason ?? '');
  }

  /**
   * Fuzzy name-match (tio-api#8054): the document was readable but the
   * borrower name is only CLOSE to the employee's — the ask is a yes/no
   * confirmation in the drawer, not another upload, so the card swaps its
   * subtitle/CTA copy and drops the upload checklist.
   */
  get isNameConfirmVariant() {
    return this.args.state.action === 'confirm_name_match';
  }

  /**
   * States that aren't asking for an upload (confirm-name and hard-ineligible
   * → contact support) explain THEMSELVES in the subtitle via the reason's
   * mapped description — e.g. name_mismatch: "The borrower name on the
   * document does not match your profile." — instead of the generic "upload a
   * recent loan document" pitch, and drop the upload checklist (instructions
   * for an action the card doesn't offer).
   */
  get showReasonSubtitle() {
    return this.isNameConfirmVariant || this.showContactSupportAction;
  }

  get reasonDescriptionKey() {
    return presentationForSlrLoanStanding(this.args.state).descriptionKey;
  }

  get showInstructionPanel() {
    return !this.showReasonSubtitle;
  }

  get uploadCtaKey() {
    return this.isNameConfirmVariant
      ? 'slr.loan_standing.actions.confirm_name_match'
      : 'slr.loan_standing.actions.upload_document';
  }

  /**
   * Audit-month presentation (Figma: Notification/Audit Month): the punchier
   * "unblock your contributions" headline plus period-aware subtitle and the
   * past-due chip. Active only when the backend supplies audit-window context
   * (RFC 012) — dark everywhere until then, same pattern as the required-by chip.
   */
  get hasAuditContext() {
    const { auditRequiredBy, auditPastDue, contributionPeriodEnd } = this.args.state;
    return !!(auditRequiredBy || auditPastDue || contributionPeriodEnd);
  }

  get titleKey() {
    // Figma v3 (SLR // Loan Standing Verification, node 767-7670): the fuzzy
    // name-match card leads with the problem, not the generic verify ask.
    if (this.isNameConfirmVariant) return 'slr.loan_standing.attention.name_confirm_title';
    return this.hasAuditContext
      ? 'slr.loan_standing.attention.audit_title'
      : 'slr.loan_standing.attention.title';
  }

  /**
   * "now–December" period label for the audit-month subtitle. Date-only
   * strings are parsed as LOCAL dates — `new Date('2026-12-01')` is UTC
   * midnight, which renders the PREVIOUS month in western timezones.
   */
  get contributionPeriodEndMonth() {
    const parsed = parseLocalish(this.args.state.contributionPeriodEnd);
    return parsed && this.intl.formatDate(parsed, { month: 'long' });
  }

  /**
   * Short localized deadline for the required-by chip ("07/30" per the Figma),
   * from the ISO auditRequiredBy value. Falls back to the raw string for
   * unparseable input rather than hiding a deadline the backend asserted.
   */
  get auditRequiredByLabel() {
    const raw = this.args.state.auditRequiredBy;
    if (!raw) return null;
    const parsed = parseLocalish(raw);
    return parsed ? this.intl.formatDate(parsed, { month: '2-digit', day: '2-digit' }) : raw;
  }

  get isProcessing() {
    return this.args.state.status === 'processing';
  }

  get isVerified() {
    return this.args.state.status === 'eligible';
  }

  get documentLabelKey() {
    return this.isNsldsVariant
      ? 'slr.loan_standing.attention.nslds_file'
      : 'slr.loan_standing.attention.loan_verification_document';
  }

  /**
   * The action row's label. The confirm variant is not asking for a document —
   * the document is already in hand and only the borrower name needs a yes/no —
   * so it reuses the drawer's confirmation question instead of a document name.
   */
  get actionRowLabelKey() {
    return this.isNameConfirmVariant
      ? 'slr.loan_standing.drawer.confirm_name_prompt'
      : this.documentLabelKey;
  }

  /**
   * Upload opens the drawer only for the actions it can actually serve
   * (uploads + borrower-name confirmation). Hard-ineligible states map to
   * contact_support and get the support link instead — never a dead-end
   * drawer whose uploader is disabled.
   */
  get showUploadAction() {
    return ['upload_document', 'upload_newer_document', 'confirm_name_match'].includes(
      this.args.state.action
    );
  }

  get showContactSupportAction() {
    return this.args.state.action === 'contact_support';
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  <template>
    {{#if this.isVerified}}
      <Card @padding="md" data-test-slr-loan-standing-success ...attributes>
        <div class="tio-col-tight">
          <Text @weight="semibold">{{t "slr.loan_standing.attention.success_title"}}</Text>
          <Text @size="sm" @variant="muted">
            {{t "slr.loan_standing.attention.success_body"}}
          </Text>
        </div>
      </Card>
    {{else}}
      <Card @padding="none" data-test-slr-loan-standing-attention-card ...attributes>
        <div class="tio-loan-standing-attention p-4">
          <div class="tio-loan-standing-attention__header">
            <span class="tio-loan-standing-attention__icon">
              <PhSealWarning @size={{24}} @weight="duotone" />
            </span>
            <div class="tio-col-tight flex-1">
              <div class="tio-row-tight flex-wrap">
                <Text @weight="semibold" data-test-slr-loan-standing-attention-title>
                  {{t this.titleKey}}
                </Text>
                {{#if @state.auditPastDue}}
                  <Chip
                    @intent="danger"
                    @appearance="faded"
                    @size="sm"
                    data-test-slr-loan-standing-past-due
                  >
                    {{t "slr.loan_standing.attention.past_due"}}
                  </Chip>
                {{else if this.auditRequiredByLabel}}
                  <Chip
                    @intent="danger"
                    @appearance="faded"
                    @size="sm"
                    data-test-slr-loan-standing-required-by
                  >
                    {{t "slr.loan_standing.attention.required_by" date=this.auditRequiredByLabel}}
                  </Chip>
                {{/if}}
              </div>
              <Text @size="sm" @variant="muted">
                {{#if this.showReasonSubtitle}}
                  {{t this.reasonDescriptionKey}}
                {{else if this.contributionPeriodEndMonth}}
                  {{t
                    "slr.loan_standing.attention.audit_subtitle"
                    periodEnd=this.contributionPeriodEndMonth
                  }}
                {{else}}
                  {{t "slr.loan_standing.attention.subtitle"}}
                {{/if}}
              </Text>
            </div>
            {{#if this.showInstructionPanel}}
              <Button
                @appearance="minimal"
                @size="sm"
                @ariaLabel={{t
                  (if
                    this.isExpanded
                    "slr.loan_standing.attention.collapse"
                    "slr.loan_standing.attention.expand"
                  )
                }}
                aria-expanded="{{this.isExpanded}}"
                data-test-slr-loan-standing-attention-toggle
                {{on "click" this.toggleExpanded}}
              >
                {{#if this.isExpanded}}
                  <PhCaretUp @size={{18}} />
                {{else}}
                  <PhCaretDown @size={{18}} />
                {{/if}}
              </Button>
            {{/if}}
          </div>

          {{#if (and this.isExpanded this.showInstructionPanel)}}
            <div
              class="tio-loan-standing-attention__panel"
              data-test-slr-loan-standing-instructions
            >
              {{#if this.isNsldsVariant}}
                <Text @size="sm">
                  <strong>{{t "slr.loan_standing.attention.nslds_intro_lead"}}</strong>
                  {{t "slr.loan_standing.attention.nslds_intro_rest"}}
                </Text>
                <ol class="tio-loan-standing-attention__steps">
                  <li>
                    <strong>{{t "slr.loan_standing.attention.nslds_step_login_label"}}</strong>
                    {{t "slr.loan_standing.attention.nslds_step_login"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.nslds_step_download_label"}}</strong>
                    {{t "slr.loan_standing.attention.nslds_step_download"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.nslds_step_upload_label"}}</strong>
                    {{t "slr.loan_standing.attention.nslds_step_upload"}}
                  </li>
                </ol>
              {{else}}
                <Text @size="sm" @weight="semibold">
                  {{t "slr.loan_standing.attention.checklist_heading"}}
                </Text>
                <ol class="tio-loan-standing-attention__steps">
                  <li>
                    <strong>{{t "slr.loan_standing.attention.check_recent_label"}}</strong>
                    {{t "slr.loan_standing.attention.check_recent"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.check_name_label"}}</strong>
                    {{t "slr.loan_standing.attention.check_name"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.check_details_label"}}</strong>
                    {{t "slr.loan_standing.attention.check_details"}}
                  </li>
                  <li>
                    <strong>{{t "slr.loan_standing.attention.check_status_label"}}</strong>
                    {{t "slr.loan_standing.attention.check_status"}}
                  </li>
                </ol>
              {{/if}}
              <Text @size="sm">
                <strong>{{t "slr.loan_standing.attention.note_label"}}</strong>
                {{t "slr.loan_standing.attention.note"}}
              </Text>
            </div>
          {{/if}}

          <div class="tio-col-tight">
            {{#if this.isReviewPanelOpen}}
              <div
                class="tio-loan-standing-attention__panel tio-col-tight"
                data-test-slr-review-name-panel
              >
                <Text @weight="semibold">{{t "slr.loan_standing.attention.review_name"}}</Text>
                <Text @size="sm">{{t "slr.loan_standing.attention.review_name_explainer"}}</Text>

                {{#if @state.employerProvidedName}}
                  <div class="tio-col-extra-tight" data-test-slr-review-employer-name>
                    <Text @size="sm" @weight="semibold">
                      {{t "slr.loan_standing.attention.name_provided_by_employer"}}
                    </Text>
                    <Text @size="sm">{{@state.employerProvidedName}}</Text>
                  </div>
                {{/if}}

                {{#if @state.documentName}}
                  <div
                    class="rounded-lg border tio-border-default tio-bg-surface px-3 py-2 tio-col-extra-tight"
                    data-test-slr-review-document-name
                  >
                    <Text @size="sm" @variant="muted">
                      {{t "slr.loan_standing.attention.name_found_in_document"}}
                    </Text>
                    <Text>{{@state.documentName}}</Text>
                  </div>
                {{/if}}

                <Text @size="sm" @weight="semibold">
                  {{t "slr.loan_standing.drawer.confirm_name_prompt"}}
                </Text>
                <RadioGroup
                  @options={{this.reviewOptions}}
                  @value={{this.reviewAnswer}}
                  @onChange={{this.setReviewAnswer}}
                  data-test-slr-review-radio-group
                />
                <div class="tio-flex-tight">
                  <Button
                    @appearance="outlined"
                    @intent="default"
                    @isDisabled={{this.confirmSelection.isRunning}}
                    data-test-slr-review-cancel-button
                    {{on "click" this.closeReviewPanel}}
                  >
                    {{t "cancel"}}
                  </Button>
                  <Button
                    @intent="primary"
                    @isRunning={{this.confirmSelection.isRunning}}
                    @isDisabled={{this.isConfirmDisabled}}
                    data-test-slr-review-confirm-button
                    {{on "click" this.confirmSelection.perform}}
                  >
                    {{t "slr.loan_standing.attention.review_confirm"}}
                  </Button>
                </div>
              </div>
            {{else}}
              {{#unless this.isNameConfirmVariant}}
                <div class="tio-row-tight">
                  <Text @weight="semibold">
                    {{t "slr.loan_standing.attention.required_documents"}}
                  </Text>
                  <Text @size="sm" @variant="muted">
                    {{t "slr.loan_standing.attention.supported_format"}}
                  </Text>
                </div>
              {{/unless}}
              <div class="tio-loan-standing-attention__doc-row" data-test-slr-loan-standing-doc-row>
                <div class="tio-row-tight">
                  <Text>{{t this.actionRowLabelKey}}</Text>
                  {{#if this.isProcessing}}
                    <Chip
                      @intent="brand"
                      @appearance="faded"
                      @size="sm"
                      data-test-slr-loan-standing-doc-analyzing
                    >
                      <PhSparkle @size={{12}} />
                      {{t "slr.loan_standing.attention.analyzing"}}
                    </Chip>
                  {{/if}}
                </div>
                {{#unless this.isProcessing}}
                  {{#if this.showUploadAction}}
                    <Button
                      @intent="primary"
                      @appearance="outlined"
                      @size="sm"
                      data-test-slr-loan-standing-doc-upload
                      {{on "click" this.primaryCtaAction}}
                    >
                      {{t this.uploadCtaKey}}
                    </Button>
                  {{else if this.showContactSupportAction}}
                    <LinkTo
                      @route="authenticated.contact-us"
                      class="tio-text-sm-medium tio-fg-info underline whitespace-nowrap"
                      data-test-slr-loan-standing-contact-support
                    >
                      {{t "slr.loan_standing.actions.contact_support"}}
                    </LinkTo>
                  {{/if}}
                {{/unless}}
              </div>
              {{#if this.isNsldsVariant}}
                <div
                  class="tio-loan-standing-attention__doc-row tio-loan-standing-attention__doc-row--accepted"
                  data-test-slr-loan-standing-first-doc-row
                >
                  <Text>{{t "slr.loan_standing.attention.loan_verification_document"}}
                    <span class="sr-only">{{t "slr.loan_standing.attention.verified"}}</span></Text>
                  <span class="tio-loan-standing-attention__accepted-icon" aria-hidden="true">
                    <PhCheckCircle @size={{20}} @weight="duotone" />
                  </span>
                </div>
              {{/if}}
            {{/if}}
          </div>
        </div>
      </Card>
    {{/if}}
  </template>
}
